/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  useEffect, useRef, useState,
} from 'react';
import L, { MarkerClusterGroup, Map, LatLng } from 'leaflet';
import { useStore } from '../../../../store';
import { IRecordMap } from '../../../../store/modules/report/i-report';
import { reconcilePopupFields } from '../popup-fields';
import { useActions } from '../../../providers/actions-provider/actions.context';
import { useLocalStorage } from '../../../providers/local-storage-provider/local-storage.context';
import { useAssetIcon } from '../../../hooks/useAssetIcon';

export interface IConfigVisibility {
  clusteredPinsByZone: boolean;
  clusteredPins: boolean;
  image: boolean;
  points: boolean;
  pointLabels: boolean;
  readerips: boolean;
  readerNames: boolean;
  antennas: boolean;
  antennaLabels: boolean;
  connections: boolean;
  zones: boolean;
  zoneLabels: boolean;
  xyAxes: boolean;
  xyAxesLabels: boolean;
  xyGrid: boolean;
  llAxes: boolean;
  llAxesLabels: boolean;
  llGrid: boolean;
  cor: boolean;
  mapBoundingBox: boolean;
  thetaDefinition: boolean;
  pointCircles: boolean;
  vectors: boolean;
  vectors2: boolean;
  mapVisibilityControls: boolean;
  snailTrail: boolean;
}
export interface IConfigTransparency {
  image: number;
  llgrid: number;
  points: number;
  xygrid: number;
  zones: number;
  xyaxis: number;
}
export interface IConfigurationValues {
  visibility: IConfigVisibility;
  transparency: IConfigTransparency;
  orientation: string; // 'yup' | 'nup'
  pageSize: number;
  drawerCollapsed: boolean;
  popupFields?: string[];
}

export const DEFAULT_POPUP_FIELD_KEYS = ['EPC', 'UID', 'Zone', 'X', 'Y', 'x', 'y'];

export interface IMapComponent {
  setBearing: (value: number) => void;
  getCenter: () => LatLng;
  getZoom: () => number;
}

export const FACILITY_KEY_PREFIX = 'facility:';

const FACILITY_KEY_PATTERN = /^facility:[0-9]+$/;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useMapContextHook = () => {
  const {
    mapReport, mapReportDefinition, executedReport, mapImageImage, mapOrigin,
  } = useStore();
  const {
    config: {
      visibility, transparency, orientation, pageSize, drawerCollapsed, popupFields,
    },
  } = mapReportDefinition.data;
  const { reportActions } = useActions();
  const originGroupId = Number(mapOrigin.data.group_id);
  const facilityKey = (Number.isFinite(originGroupId) && originGroupId > 0)
    ? `${FACILITY_KEY_PREFIX}${originGroupId}`
    : '';
  const savedViewState = facilityKey
    ? mapReportDefinition.data.config.viewStates?.[facilityKey]
    : undefined;
  const [groupSelected, setGroupSelected] = useState<{
    selectedGroup: IRecordMap[];
    notSelectedGroup: IRecordMap[];
  }>({
    selectedGroup: [],
    notSelectedGroup: [],
  });
  const localStorageService = useLocalStorage();

  const [selectedPin, setSelectedPin] = useState < IRecordMap | null >(null);
  const { url: selectedPinIconUrl } = useAssetIcon(selectedPin?.SKU);
  const [skip, setSkip] = useState<number>(0);
  const [openConfigurationDrawer, setOpenConfigurationDrawer] = useState(false);
  const [showAssetDetails, setShowAssetDetails] = useState<boolean>(false);
  const [pinNav, setPinNav] = useState<boolean>(false);
  const [hasAssetFilter, setHasAssetFilter] = useState<boolean>(false);
  const mapRef = useRef<IMapComponent>(null);
  const clusterGroupRef = useRef(null);
  const [imageAngle, setImageAngle] = useState<number>(Number(mapOrigin.data.azimuth) || 0);

  const LAT_LEN = 111320; // meters in 1° latitude (approximately)
  const LON_LEN = LAT_LEN * Math.cos((Number(mapOrigin.data.latitude) * Math.PI) / 180);

  const [rangePerDegree, setRangePerDegree] = useState< {lat:number; lon:number}>({
    lat: mapOrigin.data.units === 'ft' ? 364000 : LAT_LEN,
    lon: mapOrigin.data.units === 'ft' ? 288200 : LON_LEN,
  });

  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  const stored = localStorageService.get('PIN_DIGITS');
  const pinDigitsInitial: number = !Number.isNaN(Number(stored)) && Number(stored) > 0
    ? Number(stored) : 6;
  const [pinDigits, setPinDigits] = useState<number>(pinDigitsInitial);

  const pageSizeOptions = ['50', '100', '200', '300', '400', '500', '1000', '2000',
    '3000', '4000', '5000', '10000', '20000', '30000', '40000', '50000'];

  const [configurationValues, setConfigurationsValues] = useState<IConfigurationValues>({
    visibility: {
      clusteredPinsByZone: visibility?.clusteredPinsByZone || false,
      clusteredPins: visibility?.clusteredPins || false,
      image: visibility?.image || false,
      points: visibility?.points || false,
      pointLabels: visibility?.pointLabels || false,
      readerips: visibility?.readerips || false,
      readerNames: visibility?.readerNames || false,
      antennas: visibility?.antennas || false,
      antennaLabels: visibility?.antennaLabels || false,
      connections: visibility?.connections || false,
      zones: visibility?.zones || false,
      zoneLabels: visibility?.zoneLabels || false,
      xyAxes: visibility?.xyAxes || false,
      xyAxesLabels: visibility?.xyAxesLabels || false,
      xyGrid: visibility?.xyGrid || false,
      llAxes: visibility?.llAxes || false,
      llAxesLabels: visibility?.llAxesLabels || false,
      llGrid: visibility?.llGrid || false,
      cor: visibility?.cor || false,
      mapBoundingBox: visibility?.mapBoundingBox || false,
      thetaDefinition: visibility?.thetaDefinition || false,
      pointCircles: visibility?.pointCircles || false,
      vectors: visibility?.vectors || false,
      vectors2: visibility?.vectors2 || false,
      mapVisibilityControls: visibility?.mapVisibilityControls ?? true,
      snailTrail: visibility?.snailTrail || false,
    },
    transparency: {
      image: transparency?.image || 0,
      llgrid: transparency?.llgrid || 0,
      points: transparency?.points || 0,
      xygrid: transparency?.xygrid || 0,
      zones: transparency?.zones || 0,
      xyaxis: transparency?.xyaxis || 0,
    },
    orientation: orientation || 'yup',
    pageSize: pageSize || 1000,
    drawerCollapsed: drawerCollapsed || false,
    popupFields,
  });

  const saveControlsStateHandler = () => {
    const center = mapRef.current?.getCenter?.();
    const zoom = mapRef.current?.getZoom?.();
    const storedViewStates = mapReportDefinition.data.config.viewStates || {};
    const prevViewStates = Object.fromEntries(
      Object.entries(storedViewStates).filter(([key]) => FACILITY_KEY_PATTERN.test(key)),
    );
    const viewStates = (facilityKey && center && Number.isFinite(zoom))
      ? {
        ...prevViewStates,
        [facilityKey]: { pan: [center.lat, center.lng] as [number, number], zoom: zoom as number },
      }
      : prevViewStates;
    const controlsData = {
      id: mapReportDefinition.data.id,
      name: mapReportDefinition.data.name,
      reportdef_id: mapReportDefinition.data.reportdef_id,
      config: {
        expertOptions: mapReportDefinition.data.config.expertOptions,
        orientation: configurationValues.orientation || mapReportDefinition.data.config.orientation,
        visibility: {
          ...configurationValues.visibility,
        },
        transparency: {
          ...configurationValues.transparency,
        },
        pageSize: configurationValues.pageSize || mapReportDefinition.data.config.pageSize,
        drawerCollapsed: configurationValues.drawerCollapsed
          ?? mapReportDefinition.data.config.drawerCollapsed,
        popupFields: configurationValues.popupFields,
        viewStates,
      },
    };
    if (mapReportDefinition.data.id) {
      reportActions.editMapReportDefinition(
        mapReportDefinition.data.id,
        controlsData,
      );
    } else if (executedReport.data.id) {
      reportActions.newMapReportDefinition(
        {
          reportdef_id: executedReport.data.id,
          name: executedReport.data.name,
        },
        controlsData,
      );
    }
  };

  const changeStyle = (uniqueKey:string) => {
    document.querySelectorAll('.custom-pin-icon  svg path').forEach((element) => {
      element.setAttribute('fill', '#001D66');
      element.setAttribute('stroke', '#001D66');
    });
    const element = document.querySelector(`.custom-marker-${uniqueKey} svg path`);
    if (element) {
      element.setAttribute('fill', '#3800EE');
      element.setAttribute('stroke', '#3800EE');
    }
  };

  const rotatePoint = (
    point: [number, number],
    center: [number, number],
    angle: number,
  ): [number, number] => {
    const angleRad = (angle * Math.PI) / 180;

    // Calculate deltas
    const deltaX = point[0] - center[0];
    const deltaY = point[1] - center[1];

    // Apply rotation
    const rotatedX = deltaX * Math.cos(angleRad) - deltaY * Math.sin(angleRad);
    const rotatedY = deltaX * Math.sin(angleRad) + deltaY * Math.cos(angleRad);

    // Return the rotated point relative to the center
    return [center[0] + rotatedX, center[1] + rotatedY];
  };

  const rotateMapPerAzimuth = () => {
    setConfigurationsValues((prevState) => ({ ...prevState, orientation: 'yup' }));
    if (!mapRef?.current?.setBearing) return;
    mapRef?.current?.setBearing?.(-Number(mapOrigin.data.azimuth) || 0);
  };

  const resetMapRotation = () => {
    setConfigurationsValues((prevState) => ({ ...prevState, orientation: 'nup' }));
    if (!mapRef?.current?.setBearing) return;
    mapRef?.current?.setBearing?.(0);
  };

  const rangeToCoordinates = (rangeX: number, rangeY: number): [number, number] => {
    if (!rangePerDegree) return [Number(mapOrigin.data.latitude), Number(mapOrigin.data.longitude)];

    const latitude = Number(mapOrigin.data.latitude) + rangeY / rangePerDegree.lat;
    const longitude = Number(mapOrigin.data.longitude) + rangeX / rangePerDegree.lon;

    return [latitude, longitude];
  };

  const flyToMarker = (X: number, Y: number, uniqueKey: string) => {
    setPinNav(true);

    const position = rangeToCoordinates(Number(X), Number(Y));
    const rotatePosition:[number, number] = rotatePoint(
      [position[0], position[1]],
      [Number(mapOrigin.data.latitude), Number(mapOrigin.data.longitude)],
      imageAngle,
    );
    if (mapRef.current && clusterGroupRef.current) {
      const map:Map = mapRef.current;
      const clusterGroup: MarkerClusterGroup = clusterGroupRef.current;
      const targetLatLng = L.latLng(rotatePosition[0], rotatePosition[1]);

      map.flyTo(targetLatLng, 23, {
        animate: true,
        duration: 0.25,
      });

      setTimeout(() => {
        const cluster = clusterGroup
          ?.getLayers()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ?.find((layer) => (layer as any).options?.options
            ?.marker?.uniqueKey === uniqueKey)?.__parent;

        if (cluster) {
          cluster._icon?.click();
          setPinNav(false);
        }
      }, 2000);
    }
  };

  const onClusterClick = (markers: IRecordMap[]) => {
    setGroupSelected({
      selectedGroup: markers,
      notSelectedGroup: Object.values(mapReport.data.recordsMap)
        .filter((item) => !markers.includes(item)),
    });
  };

  const onSkipSelectedGroup = () => {
    setGroupSelected({
      selectedGroup: [],
      notSelectedGroup: [],
    });
  };

  const onConfigurationDrawerHandler = () => {
    if (executedReport.data.id && !executedReport.loading) {
      reportActions.getExecutedReport(executedReport.data.id);
    }
    if (openConfigurationDrawer) {
      setConfigurationsValues((prevState) => ({
        ...prevState,
        drawerCollapsed: true,
      }));
    }
    setOpenConfigurationDrawer(true);
    setConfigurationsValues((prevState) => ({
      ...prevState,
      drawerCollapsed: false,
    }));
  };

  const onSelectPin = (item: IRecordMap | null) => {
    setSelectedPin(item);
    changeStyle(item?.uniqueKey);
  };

  const onShowAssetDetails = (item: IRecordMap | null) => {
    onSelectPin(item);
    setShowAssetDetails(true);
    setOpenConfigurationDrawer(false);
    setConfigurationsValues((prevState) => ({
      ...prevState,
      drawerCollapsed: false,
    }));
  };

  const onCloseAssetDetails = () => {
    setShowAssetDetails(false);
    onSelectPin(null);
  };

  const updateVisibilityValue = (key: keyof IConfigVisibility, value: boolean) => {
    setConfigurationsValues((prevState) => ({
      ...prevState,
      visibility: {
        ...prevState.visibility,
        [key]: value,
      },
    }));
  };

  const updateTransparencyValue = (key: keyof IConfigTransparency, value: number) => {
    setConfigurationsValues((prevState) => ({
      ...prevState,
      transparency: {
        ...configurationValues.transparency,
        [key]: value,
      },
    }));
  };

  const updatePageSize = (value: number) => {
    setConfigurationsValues((prevState) => ({
      ...prevState,
      pageSize: value,
    }));
  };

  const updatePopupFields = (key: string, checked: boolean, seed: string[]) => {
    setConfigurationsValues((prevState) => {
      const current = prevState.popupFields ?? seed;
      let next: string[];
      if (checked) {
        next = current.includes(key) ? current : [...current, key];
      } else {
        next = current.filter((item) => item !== key);
      }
      return { ...prevState, popupFields: next };
    });
  };

  const setAllPopupFields = (fields: string[]) => {
    setConfigurationsValues((prevState) => ({ ...prevState, popupFields: fields }));
  };

  const onMapButtonClick = () => {
    updateVisibilityValue('mapVisibilityControls', !configurationValues.visibility.mapVisibilityControls);
  };

  const onZoneButtonClick = () => {
    updateVisibilityValue('zones', !configurationValues.visibility.zones);
    updateVisibilityValue('zoneLabels', !configurationValues.visibility.zones);
  };

  const onAntenaButtonClick = () => {
    updateVisibilityValue('antennas', !configurationValues.visibility?.antennas);
    updateVisibilityValue('antennaLabels', !configurationValues.visibility?.antennas);
  };
  const onImageButtonClick = () => {
    updateVisibilityValue('image', !configurationValues.visibility?.image);
  };

  const onPinButtonClick = () => {
    updateVisibilityValue('points', !configurationValues.visibility?.points);
  };
  const onXYAxesButtonClick = () => {
    updateVisibilityValue('xyAxes', !configurationValues.visibility?.xyAxes);
    updateVisibilityValue('xyAxesLabels', !configurationValues.visibility?.xyAxes);
  };
  const onLLAxesButtonClick = () => {
    updateVisibilityValue('llAxes', !configurationValues.visibility?.llAxes);
    updateVisibilityValue('llGrid', !configurationValues.visibility?.llAxes);
    updateVisibilityValue('llAxesLabels', !configurationValues.visibility?.llAxes);
  };

  const onCollapseAssetBar = () => {
    setConfigurationsValues((prevState) => ({
      ...prevState,
      drawerCollapsed: !prevState.drawerCollapsed,
    }));
  };

  useEffect(() => {
    setConfigurationsValues({
      visibility,
      transparency,
      orientation,
      pageSize: pageSize || 1000,
      drawerCollapsed: drawerCollapsed || false,
      popupFields,
    } as IConfigurationValues);
  }, [visibility, transparency, orientation, pageSize, drawerCollapsed, popupFields]);

  useEffect(() => {
    setConfigurationsValues((prevState) => {
      const next = reconcilePopupFields(
        prevState.popupFields,
        executedReport.data.columns,
        (executedReport.data.columns?.length ?? 0) > 0,
      );
      return next === prevState.popupFields ? prevState : { ...prevState, popupFields: next };
    });
  }, [executedReport.data.columns, configurationValues.popupFields]);

  const getImageDimensions = (blob: Blob):
  Promise<{ width: number; height: number }> => new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(objectUrl); // Clean up the object URL
    };

    img.onerror = (error) => {
      reject(error);
      URL.revokeObjectURL(objectUrl); // Clean up even on error
    };

    img.src = objectUrl; // Start loading the image
  });

  const getDem = (file:Blob) => {
    if (file) {
      getImageDimensions(file)
        .then((dims) => setDimensions(dims))
        // eslint-disable-next-line no-console
        .catch((error) => console.error('Error loading image:', error));
    }
  };

  useEffect(() => {
    setImageAngle(Number(mapOrigin.data.azimuth) || 0);
  }, [mapOrigin.data.azimuth, mapOrigin.data.units]);

  useEffect(() => {
    setRangePerDegree({
      lat: mapOrigin.data.units === 'ft' ? 364000 : LAT_LEN,
      lon: mapOrigin.data.units === 'ft' ? 288200 : LON_LEN,
    });
  }, [mapOrigin.data.units, mapOrigin.data.latitude]);

  useEffect(() => {
    if (!selectedPin) return;
    changeStyle(selectedPin.uniqueKey);
  }, [pinNav]);

  useEffect(() => {
    const file = mapImageImage.data;
    if (!(file instanceof Blob)) return;
    getDem(file);
  }, [mapImageImage.data]);

  useEffect(() => {
    if (mapReportDefinition.data.config) {
      setConfigurationsValues({
        visibility: {
          clusteredPinsByZone: visibility?.clusteredPinsByZone || false,
          clusteredPins: visibility?.clusteredPins || false,
          image: visibility?.image || false,
          points: visibility?.points || false,
          pointLabels: visibility?.pointLabels || false,
          readerips: visibility?.readerips || false,
          readerNames: visibility?.readerNames || false,
          antennas: visibility?.antennas || false,
          antennaLabels: visibility?.antennaLabels || false,
          connections: visibility?.connections || false,
          zones: visibility?.zones || false,
          zoneLabels: visibility?.zoneLabels || false,
          xyAxes: visibility?.xyAxes || false,
          xyAxesLabels: visibility?.xyAxesLabels || false,
          xyGrid: visibility?.xyGrid || false,
          llAxes: visibility?.llAxes || false,
          llAxesLabels: visibility?.llAxesLabels || false,
          llGrid: visibility?.llGrid || false,
          cor: visibility?.cor || false,
          mapBoundingBox: visibility?.mapBoundingBox || false,
          thetaDefinition: visibility?.thetaDefinition || false,
          pointCircles: visibility?.pointCircles || false,
          vectors: visibility?.vectors || false,
          vectors2: visibility?.vectors2 || false,
          mapVisibilityControls: visibility?.mapVisibilityControls ?? true,
          snailTrail: visibility?.snailTrail || false,
        },
        transparency: {
          image: transparency?.image || 0,
          llgrid: transparency?.llgrid || 0,
          points: transparency?.points || 0,
          xygrid: transparency?.xygrid || 0,
          zones: transparency?.zones || 0,
          xyaxis: transparency?.xyaxis || 0,
        },
        orientation: orientation || 'yup',
        pageSize: pageSize || 1000,
        drawerCollapsed: drawerCollapsed || false,
        popupFields,
      });
    }
  }, [transparency, visibility, orientation, pageSize, drawerCollapsed, popupFields]);

  return {
    selectedPin,
    selectedPinIconUrl,
    onSelectPin,
    showAssetDetails,
    onShowAssetDetails,
    onCloseAssetDetails,
    onPinButtonClick,
    onMapButtonClick,
    onClusterClick,
    groupSelected,
    onSkipSelectedGroup,
    onConfigurationDrawerHandler,
    openConfigurationDrawer,
    onImageButtonClick,
    mapRef,
    flyToMarker,
    clusterGroupRef,
    configurationValues,
    setConfigurationsValues,
    pageSize,
    skip,
    setSkip,
    pageSizeOptions,
    onAntenaButtonClick,
    onZoneButtonClick,
    saveControlsStateHandler,
    updateVisibilityValue,
    updateTransparencyValue,
    updatePageSize,
    updatePopupFields,
    setAllPopupFields,
    onXYAxesButtonClick,
    onLLAxesButtonClick,
    dimensions,
    imageAngle,
    setImageAngle,
    collapseAssetBar: configurationValues.drawerCollapsed,
    onCollapseAssetBar,
    rangePerDegree,
    rotatePoint,
    rangeToCoordinates,
    setPinDigits,
    pinDigits,
    setOpenConfigurationDrawer,
    rotateMapPerAzimuth,
    resetMapRotation,
    hasAssetFilter,
    setHasAssetFilter,
    facilityKey,
    savedViewState,
  };
};
